import { useCallback } from "react";

import { CharacteristicData, IFormContext, State } from "..";
import { usePrompt } from "../../../../hooks";
import { OverrideContent } from "../OverrideContent";

interface AcceptParams {
  state: State;
  setState: React.Dispatch<React.SetStateAction<State>>;
}

type useCharDataHooks = (
  params: AcceptParams
) => Pick<
  IFormContext,
  | "updateCharData"
  | "buildNewCharData"
  | "handleManyCharData"
  | "getOneData"
  | "getManyData"
  | "overrideCharacteristicData"
  | "isOverrideAvailable"
>;

//if investor_id === null its mean global characteristic
//if investor_id exists its mean characteristic for specific investor

export const useCharDataHooks: useCharDataHooks = ({ state, setState }) => {
  const openPrompt = usePrompt();
  const buildNewCharData = (
    params: Pick<CharacteristicData, "characteristic_id"> &
      Partial<Pick<CharacteristicData, "currency" | "percentage" | "value" | "investor_id" | "round_id">>
  ): CharacteristicData => {
    // Find max id of the characteristics data
    const maxId = state.data.reduce((acc, item) => {
      const numberId = Number(item.id);
      return numberId > acc ? numberId : acc;
    }, 0);

    // Get deal_id from existing data or use 0 as fallback
    const deal_id = state.data[0]?.deal_id || 0;

    const charData: CharacteristicData = {
      id: String(maxId + 1),
      deal_id,
      is_default: false,
      percentage: 100,
      value: "",
      currency: null,
      is_deleted: false,
      investor_id: params.investor_id || null,
      round_id: params.round_id || null,
      ...params
    };

    return charData;
  };

  // const updateSingleCharData = (params: Pick<CharacteristicData, "id"> & Partial<Pick<CharacteristicData, "currency" | "percentage" | "value">>) => {
  //   return setS
  // };

  const updateCharData = useCallback<IFormContext["updateCharData"]>(
    ({ id, investor_id, round_id, ...rest }) => {
      // if no item was found and value is not empty it means we need to created one locally
      if (!id && rest.value) {
        const newCharData = buildNewCharData({ investor_id, round_id, ...rest });
        setState((prevState) => ({
          ...prevState,
          data: [...prevState.data, newCharData]
        }));

        return; // Stop updating - add a new one
      }

      // If the value is empty and the id is a string, we need to delete the item
      if ("value" in rest && !rest.value && typeof id === "string") {
        setState((prevState) => ({
          ...prevState,
          data: prevState.data.filter((item) => !(item.id === id && item.investor_id === investor_id))
        }));

        return;
      }

      // Update existing item, preserving existing values if not explicitly provided
      // Use (id, investor_id) to find the correct record
      setState((prevState) => ({
        ...prevState,
        data: prevState.data.map((item) => {
          if (!(item.id === id && item.investor_id === investor_id && item.round_id === round_id)) return item;

          return { ...item, ...rest };
        })
      }));
    },
    [state, setState]
  );

  const handleManyCharData = useCallback<IFormContext["handleManyCharData"]>(
    (data) => {
      const res: CharacteristicData[] = [];
      // "Map" will keep only data with unique id, existing will be rewritten newly will be added.
      const dataMap = new Map<CharacteristicData["id"], CharacteristicData>();

      // Put old data in the collection
      state.data.forEach((item) => dataMap.set(item.id, item));

      // Add incoming data to the collection
      // Existing elements key=id will be replaced with a new ones
      // New elements will be added to the collection
      data.forEach((item) => dataMap.set(item.id, item));

      dataMap.forEach((value) => res.push(value));

      setState((prevState) => ({ ...prevState, data: res }));
    },
    [state, setState]
  );

  const matchesCharacteristicFilter = (
    char: CharacteristicData,
    characteristic_id: string | number,
    investor_id: number | null | undefined,
    round_id: number | null | undefined
  ): boolean => {
    if (char.characteristic_id !== characteristic_id) return false;

    // deal characteristic
    if (!investor_id && !round_id) {
      return !char.investor_id && !char.round_id;
    }

    // round/investor characteristic
    if (investor_id && round_id) {
      return char.investor_id === investor_id && char.round_id === round_id;
    }

    // investor characteristic
    if (investor_id && !round_id) {
      return char.investor_id === investor_id && char.round_id === null;
    }

    // round characteristic
    if (round_id && !investor_id) {
      return char.round_id === round_id && char.investor_id === null;
    }

    return false;
  };

  const getOneData = useCallback<IFormContext["getOneData"]>(
    ({ characteristic_id, investor_id, round_id }) => {
      return state.data.find((char) => matchesCharacteristicFilter(char, characteristic_id, investor_id, round_id));
    },
    [state]
  );

  const getManyData = useCallback<IFormContext["getManyData"]>(
    ({ characteristic_id, investor_id, round_id }) => {
      return state.data.filter((char) => matchesCharacteristicFilter(char, characteristic_id, investor_id, round_id));
    },
    [state]
  );

  const childCharacteristicDataSelector = useCallback(
    ({
      dealOverrideId,
      roundOverrideId,
      characteristicId
    }: {
      dealOverrideId?: number;
      roundOverrideId?: number;
      characteristicId: string | number;
    }) => {
      const res = state.data.filter((char) => {
        // Base condition: characteristic_id must match
        if (char.characteristic_id !== characteristicId) return false;

        // if deal_id is passed, search deal child characteristics (deal+round+round/investor+investor)
        if (dealOverrideId) {
          return char.deal_id === dealOverrideId && char.value;
        }

        // if round_id is passed (and deal_id is not), search round child characteristics (round+round/investor)
        if (roundOverrideId) {
          return char.round_id === roundOverrideId && char.value;
        }

        return false;
      });
      return res;
    },
    [state]
  );

  const overrideCharacteristicData = useCallback<IFormContext["overrideCharacteristicData"]>(
    async ({ dealId, dealOverrideId, roundOverrideId, characteristicId, anchorEl, characteristicName }) => {
      const characteristicData = childCharacteristicDataSelector({
        dealOverrideId,
        roundOverrideId,
        characteristicId
      });
      const result =
        dealId &&
        (await openPrompt({
          Component: OverrideContent,
          componentType: "popover",
          anchorEl: anchorEl as HTMLElement,
          props: {
            type: roundOverrideId ? "round" : "deal",
            characteristicData,
            characteristicName,
            dealId
          }
        }));
      if (result) {
        if (roundOverrideId) {
          //filter - leave only round/investor characteristics
          const childCharacteristicsIds = result
            .filter((item) => item.round_id === roundOverrideId && item.investor_id)
            .map((item) => item.id);

          setState((prevState) => ({
            ...prevState,
            data: prevState.data
              .filter((item) => {
                // Remove items with string id that are in childCharacteristicsIds
                if (childCharacteristicsIds.includes(item.id) && typeof item.id === "string") {
                  return false;
                }
                return true;
              })
              .map((item) => {
                if (childCharacteristicsIds.includes(item.id)) {
                  return { ...item, is_deleted: true, value: "" };
                }
                return item;
              })
          }));
        }
        if (dealOverrideId) {
          //filter - leave only round + round/investor characteristics
          const childCharacteristicsIds = result
            .filter((item) => item.deal_id === dealOverrideId && (item.investor_id || item.round_id))
            .map((item) => item.id);

          setState((prevState) => ({
            ...prevState,
            data: prevState.data
              .filter((item) => {
                // Remove items with string id that are in childCharacteristicsIds
                if (childCharacteristicsIds.includes(item.id) && typeof item.id === "string") {
                  return false;
                }
                return true;
              })
              .map((item) => {
                if (childCharacteristicsIds.includes(item.id)) {
                  return { ...item, is_deleted: true, value: "" };
                }
                return item;
              })
          }));
        }
      }
    },
    [childCharacteristicDataSelector]
  );

  const isOverrideAvailable = useCallback(
    ({
      dealOverrideId,
      roundOverrideId,
      characteristicId
    }: {
      dealOverrideId?: number;
      roundOverrideId?: number;
      characteristicId: string | number;
    }) => {
      const characteristicData = childCharacteristicDataSelector({
        dealOverrideId,
        roundOverrideId,
        characteristicId
      });
      // if there is any investor_id or round_id, then it means we can override the characteristic
      let check = false;
      if (dealOverrideId) {
        check = characteristicData.some((item) => item.investor_id || item.round_id);
      }
      if (roundOverrideId) {
        check = characteristicData.some((item) => item.investor_id);
      }
      return check;
    },
    [childCharacteristicDataSelector]
  );

  return {
    updateCharData,
    buildNewCharData,
    handleManyCharData,
    getOneData,
    getManyData,
    overrideCharacteristicData,
    isOverrideAvailable
  };
};
